import { useEffect, useRef, useState } from "preact/hooks"

export const DYNAMIC_CARD_LOADED_EVENT = "@nosto/DynamicCard/loaded"

type UseDynamicCardGridReadyOptions = {
  enabled: boolean
  expectedCount: number
  signature: string
  settleTimeoutMs?: number
}

const DEFAULT_SETTLE_TIMEOUT_MS = 5000

function isSettledDynamicCard(card: Element) {
  return (
    !card.hasAttribute("loading") &&
    (card.hasAttribute("error") ||
      card.hasAttribute("empty") ||
      card.querySelector("[data-nosto-empty-card]") != null ||
      card.innerHTML.trim().length > 0)
  )
}

export function useDynamicCardGridReady({
  enabled,
  expectedCount,
  signature,
  settleTimeoutMs = DEFAULT_SETTLE_TIMEOUT_MS
}: UseDynamicCardGridReadyOptions) {
  const gridRef = useRef<HTMLDivElement>(null)
  const [readySignature, setReadySignature] = useState<string | null>(null)
  const ready = !enabled || expectedCount === 0 || readySignature === signature

  useEffect(() => {
    if (!enabled || expectedCount === 0) {
      setReadySignature(signature)
      return
    }

    setReadySignature(null)

    const grid = gridRef.current
    if (!grid) {
      return
    }

    const loadedCards = new WeakSet<Element>()
    const getCards = () => Array.from(grid.querySelectorAll("nosto-dynamic-card"))

    const markReadyIfComplete = () => {
      const cards = getCards()
      const settledCount = cards.filter(card => loadedCards.has(card) || isSettledDynamicCard(card)).length

      if (cards.length >= expectedCount && settledCount >= expectedCount) {
        setReadySignature(signature)
      }
    }

    const handleLoaded = (event: Event) => {
      const card = event.target

      if (card instanceof Element && grid.contains(card)) {
        loadedCards.add(card)
        markReadyIfComplete()
      }
    }

    const observer = new MutationObserver(markReadyIfComplete)
    observer.observe(grid, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["loading", "error", "empty"]
    })

    const timeout = window.setTimeout(() => setReadySignature(signature), settleTimeoutMs)

    grid.addEventListener(DYNAMIC_CARD_LOADED_EVENT, handleLoaded)
    markReadyIfComplete()

    return () => {
      window.clearTimeout(timeout)
      observer.disconnect()
      grid.removeEventListener(DYNAMIC_CARD_LOADED_EVENT, handleLoaded)
    }
  }, [enabled, expectedCount, signature, settleTimeoutMs])

  return { gridRef, ready }
}
